'use client';

import { type PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { useConsent } from '@/lib/contexts/consent/ConsentProvider';
import { logger } from '@/lib/logger';

import { AnalyticsContext } from './context';
import { buildAnalyticsContext } from './enrich';
import { createPostHogPlugin } from './plugins/posthog';
import { createVialmaPlugin } from './plugins/vialma';
import { generateStreamId, resolveSessionId } from './session';

import type { AnalyticsPlugin } from './plugins/types';
import type { AnalyticsState } from './types';

interface AnalyticsProviderProps extends PropsWithChildren {
  config: Awaited<ReturnType<typeof import('@/lib/api').fetchSiteConfig>>;
}

export function AnalyticsProvider({
  children,
  config,
}: AnalyticsProviderProps) {
  const { hasConsent } = useConsent();

  const analyticsPlugins = useMemo<AnalyticsPlugin[]>(
    () => [createVialmaPlugin(), createPostHogPlugin()],
    [],
  );

  const getMuxDataKey = useCallback(
    () => config.configuration.analytics?.mux ?? null,
    [config],
  );

  const getSessionId = useCallback(() => resolveSessionId(), []);

  const track = useCallback<AnalyticsState['track']>(
    event => {
      const context = buildAnalyticsContext(config);
      const analyticsAllowed = hasConsent('analytics');

      analyticsPlugins.forEach(plugin => {
        if (plugin.name === 'posthog' && !analyticsAllowed) {
          return;
        }

        try {
          void plugin.track?.(event, context);
        } catch (error) {
          logger.error(error, { plugin: plugin.name, operation: 'track' });
        }
      });
    },
    [analyticsPlugins, config, hasConsent],
  );

  const identify = useCallback<AnalyticsState['identify']>(
    event => {
      const analyticsAllowed = hasConsent('analytics');
      if (!analyticsAllowed) return;

      const context = buildAnalyticsContext(config);

      analyticsPlugins.forEach(plugin => {
        try {
          void plugin.identify?.(event, context);
        } catch (error) {
          logger.error(error, { plugin: plugin.name, operation: 'identify' });
        }
      });
    },
    [analyticsPlugins, config, hasConsent],
  );

  const page = useCallback<AnalyticsState['page']>(
    event => {
      const analyticsAllowed = hasConsent('analytics');
      if (!analyticsAllowed) return;

      const context = buildAnalyticsContext(config);

      analyticsPlugins.forEach(plugin => {
        try {
          void plugin.page?.(event, context);
        } catch (error) {
          logger.error(error, { plugin: plugin.name, operation: 'page' });
        }
      });
    },
    [analyticsPlugins, config, hasConsent],
  );

  const [resetCount, setResetCount] = useState(0);

  const reset = useCallback<AnalyticsState['reset']>(() => {
    const context = buildAnalyticsContext(config);

    analyticsPlugins.forEach(plugin => {
      try {
        void plugin.reset?.(context);
      } catch (error) {
        logger.error(error, { plugin: plugin.name, operation: 'reset' });
      }
    });

    setResetCount(c => c + 1);
  }, [analyticsPlugins, config]);

  const registerContext = useCallback<AnalyticsState['registerContext']>(
    properties => {
      const context = buildAnalyticsContext(config);

      analyticsPlugins.forEach(plugin => {
        try {
          void plugin.registerContext?.(properties, context);
        } catch (error) {
          logger.error(error, {
            plugin: plugin.name,
            operation: 'registerContext',
          });
        }
      });
    },
    [analyticsPlugins, config],
  );

  const setGroup = useCallback<AnalyticsState['setGroup']>(
    group => {
      const context = buildAnalyticsContext(config);

      analyticsPlugins.forEach(plugin => {
        try {
          void plugin.setGroup?.(group, context);
        } catch (error) {
          logger.error(error, { plugin: plugin.name, operation: 'setGroup' });
        }
      });
    },
    [analyticsPlugins, config],
  );

  const value = useMemo<AnalyticsState>(
    () => ({
      getMuxDataKey,
      getSessionId,
      generateStreamId,
      track,
      identify,
      page,
      reset,
      resetCount,
      registerContext,
      setGroup,
    }),
    [
      getMuxDataKey,
      getSessionId,
      track,
      identify,
      page,
      reset,
      resetCount,
      registerContext,
      setGroup,
    ],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}
